// src/utils/lossRunEra.js
//
// Tenure on the coverage grid: which terms were written by us
// (AgencyOfRecord = OWN_AGENCY_NAME) and which by another agency. Two pure
// questions, both answered from FIG_Policies.AgencyOfRecord alone:
//   cellOwnership(policies) — one grid cell (line × term): mine / not mine /
//                             mixed / empty
//   eraBands(terms, byTerm)  — consecutive terms grouped by the dominant agency
// Blank AgencyOfRecord is unknown, never ours. Comparison is trimmed and
// case-insensitive, same as lossRunStatus.policyNeedsAuthorization.

import { OWN_AGENCY_NAME } from './lossRunStatus';

export const AGENCY_NOT_RECORDED = 'Agency not recorded';
export const OWN_ERA_LABEL = `${OWN_AGENCY_NAME} — your years`;

const blank = v => v === null || v === undefined || String(v).trim() === '';
export const agencyKey = v => (blank(v) ? '' : String(v).trim().toLowerCase());
const OWN_KEY = agencyKey(OWN_AGENCY_NAME);
export const isOwnAgency = v => agencyKey(v) === OWN_KEY;
const agencyName = v => (blank(v) ? AGENCY_NOT_RECORDED : String(v).trim());

/**
 * cellOwnership(policies) → { kind, mine, total, agencies, words }
 *   kind:     'empty' | 'mine' | 'notMine' | 'mixed'
 *   agencies: distinct agency names in the cell, ours first, then A–Z
 *   words:    "2 of 3 policies were written by Fragos Insurance Group" (mixed),
 *             "Written by Fragos Insurance Group" (mine),
 *             "Written by Texas First Insurance" (not mine), '' (empty)
 */
export function cellOwnership(policies) {
  const list = (policies || []).filter(Boolean);
  const total = list.length;
  if (total === 0) return { kind: 'empty', mine: 0, total: 0, agencies: [], words: '' };
  const mine = list.filter(p => isOwnAgency(p.AgencyOfRecord)).length;
  const names = new Map();
  for (const p of list) names.set(agencyKey(p.AgencyOfRecord), agencyName(p.AgencyOfRecord));
  const agencies = [...names.entries()]
    .sort((a, b) => (b[0] === OWN_KEY) - (a[0] === OWN_KEY) || a[1].localeCompare(b[1]))
    .map(([, name]) => name);
  const kind = mine === total ? 'mine' : mine === 0 ? 'notMine' : 'mixed';
  const words = kind === 'mine' ? `Written by ${OWN_AGENCY_NAME}`
    : kind === 'notMine' ? `Written by ${agencies.join(', ')}`
      : `${mine} of ${total} polic${total === 1 ? 'y was' : 'ies were'} written by ${OWN_AGENCY_NAME}`;
  return { kind, mine, total, agencies, words };
}

/**
 * dominantAgency(policies) → { key, label, own, name }
 * Most policies wins; a tie goes to our own agency, then A–Z by name.
 * No policies, or all blank → "Agency not recorded".
 */
export function dominantAgency(policies) {
  const counts = new Map();
  for (const p of (policies || []).filter(Boolean)) {
    const k = agencyKey(p.AgencyOfRecord);
    const cur = counts.get(k) || { key: k, name: agencyName(p.AgencyOfRecord), n: 0 };
    cur.n++;
    counts.set(k, cur);
  }
  const best = [...counts.values()].sort((a, b) => b.n - a.n || (b.key === OWN_KEY) - (a.key === OWN_KEY) || a.name.localeCompare(b.name))[0];
  if (!best) return { key: '', name: AGENCY_NOT_RECORDED, label: AGENCY_NOT_RECORDED, own: false };
  const own = best.key === OWN_KEY;
  return { key: best.key, name: best.name, label: own ? OWN_ERA_LABEL : best.name, own };
}

/**
 * eraBands(terms, byTerm)
 *   terms  — grid columns in order (oldest left)
 *   byTerm — Map(term -> eligible policies of that term) or a plain object
 * → [{ key, name, label, own, terms: [...], span }]  consecutive terms with
 *   the same dominant agency, merged into one band.
 */
export function eraBands(terms, byTerm) {
  const get = t => (byTerm?.get ? byTerm.get(t) : byTerm?.[t]) || [];
  const bands = [];
  for (const term of terms || []) {
    const d = dominantAgency(get(term));
    const last = bands[bands.length - 1];
    if (last && last.key === d.key) { last.terms.push(term); last.span++; continue; }
    bands.push({ key: d.key, name: d.name, label: d.label, own: d.own, terms: [term], span: 1 });
  }
  return bands;
}

/**
 * agencyTag(ownership, band) → string | null
 * The exception tag under a cell's chips: only for an UNTINTED cell
 * (kind 'notMine') whose agencies differ from the column's era band.
 * "Texas First Insurance" in a Fragos column; null when it matches the band.
 */
export function agencyTag(ownership, band) {
  if (!ownership || ownership.kind !== 'notMine') return null;
  const bandName = band ? band.name : AGENCY_NOT_RECORDED;
  const bandKey = band ? band.key : '';
  const others = ownership.agencies.filter(name => agencyKey(name === AGENCY_NOT_RECORDED ? '' : name) !== bandKey && name !== bandName);
  return others.length ? others.join(', ') : null;
}
